import type { ReactNode } from "react";

export type ToastVariant = "default" | "success" | "error" | "warning" | "info";

export type ToastContent = string | string[] | ReactNode;

export type ToastPayload = {
  id: string;
  title: string;
  description?: ToastContent;
  type: ToastVariant;
  duration: number;
  remainingMs: number;
  paused: boolean;
};

export type ToastEvent =
  | { type: "add"; toast: ToastPayload }
  | { type: "dismiss"; id?: string }
  | { type: "sync"; toasts: ToastPayload[] };

type ToastOptions = {
  description?: ToastContent;
  duration?: number;
  id?: string;
};

type ToastListener = (event: ToastEvent) => void;

/**
 * IMPORTANT: Always import toast from this canonical path to avoid duplicate module instances.
 * The toast system uses a singleton pattern; importing from different paths can cause events
 * to be emitted to one instance while a different instance is subscribed in the Toaster.
 */

const listeners = new Set<ToastListener>();

/**
 * Module-level toast store that survives React component remounts.
 * This is the source of truth for active toasts.
 */
let toastStore: ToastPayload[] = [];

type ToastTimerState = {
  timeoutId: number;
  startedAtMs: number;
};

const toastTimers = new Map<string, ToastTimerState>();

const emit = (event: ToastEvent) => {
  listeners.forEach((listener) => listener(event));
};

const createId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

const scheduleDismiss = (toastItem: ToastPayload) => {
  if (toastItem.duration <= 0 || toastItem.remainingMs <= 0) return;
  if (typeof window === "undefined") return;

  const startedAtMs = Date.now();
  const timeoutId = window.setTimeout(() => toast.dismiss(toastItem.id), toastItem.remainingMs);
  toastTimers.set(toastItem.id, { timeoutId, startedAtMs });
};

const getEffectiveRemainingMs = (toastItem: ToastPayload) => {
  if (toastItem.paused) return toastItem.remainingMs;
  const timerState = toastTimers.get(toastItem.id);
  if (!timerState) return toastItem.remainingMs;
  const elapsed = Date.now() - timerState.startedAtMs;
  return Math.max(0, toastItem.remainingMs - elapsed);
};

const syncStore = () => {
  emit({ type: "sync", toasts: toast.getToasts() });
};

const createToast = (title: string, type: ToastVariant, options?: ToastOptions) => {
  const duration = options?.duration ?? 7000;
  const toastItem: ToastPayload = {
    id: options?.id ?? createId(),
    title,
    description: options?.description,
    type,
    duration,
    remainingMs: duration,
    paused: false
  };

  // Add to module-level store
  toastStore = [...toastStore, toastItem];

  // Schedule auto-dismiss at module level (not in component)
  scheduleDismiss(toastItem);

  emit({ type: "add", toast: toastItem });
  return toastItem.id;
};

const clearTimer = (id: string) => {
  const timer = toastTimers.get(id);
  if (timer && typeof window !== "undefined") {
    window.clearTimeout(timer.timeoutId);
  }
  toastTimers.delete(id);
};

const dismissToast = (id?: string) => {
  if (id) {
    clearTimer(id);
  } else {
    toastStore.forEach((toastItem) => clearTimer(toastItem.id));
  }

  // Update module-level store
  toastStore = id ? toastStore.filter((t) => t.id !== id) : [];
  emit({ type: "dismiss", id });
};

const pauseToast = (id: string) => {
  const toastItem = toastStore.find((toastEntry) => toastEntry.id === id);
  if (!toastItem || toastItem.paused || toastItem.duration <= 0) return;

  const timerState = toastTimers.get(id);
  if (!timerState) return;

  const elapsed = Date.now() - timerState.startedAtMs;
  const remainingMs = Math.max(0, toastItem.remainingMs - elapsed);
  clearTimer(id);

  toastStore = toastStore.map((entry) =>
    entry.id === id
      ? {
          ...entry,
          remainingMs,
          paused: true
        }
      : entry
  );

  syncStore();
};

const resumeToast = (id: string) => {
  const toastItem = toastStore.find((toastEntry) => toastEntry.id === id);
  if (!toastItem || !toastItem.paused || toastItem.duration <= 0) return;

  if (toastItem.remainingMs <= 0) {
    toast.dismiss(id);
    return;
  }

  const updatedToast: ToastPayload = {
    ...toastItem,
    paused: false
  };

  toastStore = toastStore.map((entry) => (entry.id === id ? updatedToast : entry));
  scheduleDismiss(updatedToast);
  syncStore();
};

export const toast = Object.assign(
  (title: string, options?: ToastOptions) => createToast(title, "default", options),
  {
    success: (title: string, options?: ToastOptions) => createToast(title, "success", options),
    error: (title: string, options?: ToastOptions) => createToast(title, "error", options),
    warning: (title: string, options?: ToastOptions) => createToast(title, "warning", options),
    info: (title: string, options?: ToastOptions) => createToast(title, "info", options),
    dismiss: dismissToast,
    pause: pauseToast,
    resume: resumeToast,
    subscribe: (listener: ToastListener) => {
      listeners.add(listener);
      // Immediately sync current toasts to new subscriber (handles StrictMode remounts)
      if (toastStore.length > 0) {
        listener({ type: "sync", toasts: toast.getToasts() });
      }
      return () => {
        listeners.delete(listener);
      };
    },
    /** Get current toasts snapshot */
    getToasts: (): ToastPayload[] =>
      toastStore.map((toastItem) => ({
        ...toastItem,
        remainingMs: getEffectiveRemainingMs(toastItem)
      }))
  }
);

export type { ToastOptions };
